import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import { useAskRefundMutation } from '../../../../apis/mlkApi/mlk.api.hooks';
import { Button } from '../../../../components/Button/Button';
import { Modal, ModalRef } from '../../../../components/Modal/Modal';
import { useLanguage } from '../../../../locale/useLanguage';

import './RefundModal.css';

type RefundModalProps = {
  orderId: number | undefined;
  purchaseId: number | undefined;
};

export const RefundModal: React.ForwardRefExoticComponent<
  RefundModalProps & React.RefAttributes<ModalRef>
> = forwardRef(({ orderId, purchaseId }, forwardedRef) => {
  const [t] = useLanguage();
  const askRefundModalRef = useRef<ModalRef>(null);
  const successModalRef = useRef<ModalRef>(null);

  const [askRefund] = useAskRefundMutation();

  const closeModal = useCallback(() => {
    askRefundModalRef.current?.closeModal();
  }, []);
  
  const closeSuccessModal = useCallback(() => {
    successModalRef.current?.closeModal();
  }, []);

  const makeRefund = useCallback(async () => {
    if (!purchaseId) return;
    try {
      askRefundModalRef.current?.closeModal();
      await askRefund(purchaseId).unwrap();
      successModalRef.current?.openModal();
    } catch (e) {
      console.log(e);
    }
  }, [askRefund, purchaseId]);

  useImperativeHandle(forwardedRef, () => ({
    openModal: askRefundModalRef.current!.openModal,
    closeModal: askRefundModalRef.current!.closeModal,
  }));

  return (
    <>
      <Modal ref={askRefundModalRef}>
        <h3 className="modal-title">{t('orderDetails.refundModalTitle')}</h3>
        <p className="modal-description">
          {t('orderDetails.refundModalDescription', { orderId })}
        </p>
        <div className="modal-button-container">
          <Button
            onClick={makeRefund}
            label={t('orderDetails.refundModalConfirm')}
            isPrimary
          />
          <div className="refund-modal--cancel-button">
            <Button
              onClick={closeModal}
              label={t('orderDetails.refundModalCancel')}
            />
          </div>
        </div>
      </Modal>
      <Modal hasClose={false} ref={successModalRef}>
        <h3 className="modal-title">{t('orderDetails.refundModalSuccess')}</h3>
        <Button
          onClick={closeSuccessModal}
          isPrimary
          label={t('orderDetails.refundModalCancel')}
        />
      </Modal>
    </>
  );
});
